import { useState, useEffect } from 'react';
import { X, AlertTriangle } from 'lucide-react';
import api from '../lib/api';

export default function ReportModal({ type, targetId, onClose }) {
  const [reasons, setReasons] = useState([]);
  const [selectedReason, setSelectedReason] = useState('');
  const [details, setDetails] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetch = async () => {
      try {
        const { data } = await api.get('/safety/report-reasons');
        setReasons(data[type] || []);
      } catch {}
    };
    fetch();
  }, [type]);

  const handleSubmit = async () => {
    if (!selectedReason) return;
    setSubmitting(true);
    setError('');
    try {
      await api.post(`/safety/report/${type}/${targetId}`, {
        reason: selectedReason,
        details
      });
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit report');
    }
    setSubmitting(false);
  };

  if (submitted) {
    return (
      <div className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/40 p-5" onClick={onClose}>
        <div className="w-full max-w-[400px] bg-white rounded-3xl p-6 animate-scale-in shadow-2xl" onClick={e => e.stopPropagation()}>
          <div className="text-center py-4">
            <div className="w-14 h-14 rounded-full bg-[#B6E600]/15 flex items-center justify-center mx-auto mb-3">
              <AlertTriangle size={24} className="text-[#3F5200]" />
            </div>
            <h3 className="text-lg font-bold text-[#111111]">Report Submitted</h3>
            <p className="text-sm text-[#6B6E73] mt-2">Our team will review this and take appropriate action.</p>
            <button
              onClick={onClose}
              className="mt-6 bg-[#111111] text-white font-medium rounded-full px-8 py-2.5 text-sm"
              data-testid="report-done-btn"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/40 p-5" onClick={onClose}>
      <div
        className="w-full max-w-[400px] bg-white rounded-3xl animate-scale-in shadow-2xl flex flex-col max-h-[85dvh] overflow-hidden"
        onClick={e => e.stopPropagation()}
        data-testid="report-modal"
      >
        <div className="flex items-center justify-between px-5 pt-5 pb-3 flex-shrink-0">
          <h3 className="text-lg font-bold text-[#111111]">Report {type === 'user' ? 'User' : 'Plan'}</h3>
          <button onClick={onClose} className="p-1 active:scale-90 transition-transform" data-testid="report-close">
            <X size={20} className="text-[#9B9EA3]" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 pb-2">
          {error && (
            <div className="bg-red-50 border border-red-100 rounded-xl px-3 py-2 text-red-600 text-sm mb-3">{error}</div>
          )}

          <p className="text-xs text-[#6B6E73] mb-3">Select a reason for your report:</p>

          <div className="space-y-2 mb-4">
            {reasons.map(r => (
              <button
                key={r.id}
                data-testid={`reason-${r.id}`}
                onClick={() => setSelectedReason(r.id)}
                className={`w-full text-left p-3 rounded-xl text-sm transition-all btn-press ${
                  selectedReason === r.id
                    ? 'bg-[#B6E600]/10 border border-[#B6E600]/30 text-[#111111] font-medium'
                    : 'bg-[#F1F0EA] border border-transparent text-[#6B6E73]'
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>

          <textarea
            data-testid="report-details"
            value={details}
            onChange={e => setDetails(e.target.value)}
            placeholder="Additional details (optional)..."
            rows={3}
            className="w-full bg-[#F1F0EA] text-[#111111] rounded-xl px-3 py-2.5 text-sm border border-black/[0.06] focus:outline-none focus:border-[#B6E600]/50 placeholder-[#B0B3B8] resize-none"
          />
        </div>

        <div className="px-5 pt-3 pb-5 border-t border-black/[0.05] flex-shrink-0 bg-white">
          <button
            data-testid="report-submit-btn"
            onClick={handleSubmit}
            disabled={!selectedReason || submitting}
            className="w-full bg-[#FF3B30] text-white font-bold rounded-full py-3 text-sm active:scale-[0.98] transition-all disabled:opacity-40"
          >
            {submitting ? 'Submitting...' : 'Submit Report'}
          </button>
        </div>
      </div>
    </div>
  );
}
